import gql from 'graphql-tag';

export type DashboardPlan =
  SearchPlansQuery['search']['plans']['plans'][number];

// Note this must match the limit passed to searchPlans;
export const PLAN_PAGINATION_LIMIT = 5;
export const SEARCH_PLANS = gql`
  query searchPlans($accountId: Int!, $page: Int!, $statuses: [String!]) {
    search {
      plans(
        page: $page
        limit: 5
        sort: created_at
        order: ASC
        filters: { accountId: $accountId, statuses: $statuses }
      ) {
        plans {
          id
          status
          createdAt
          accountId
          displayName
          billable
          isExclusiveMembershipPlan
          reactivationEligibility {
            eligible
            reasons
          }
          mostRecentStatement {
            id
            status
            firstStatementWithSolarSavings
            lineItemSummary {
              headlineSolarSavingsRate
            }
          }
          utilityAccounts {
            id
            displayServiceAddress
            highlights
            accountNumber
            utility {
              id
              displayName
            }
          }
          vnmProductSubscription {
            id
            vnmSubscription {
              id
              status
              vnmSolarProject {
                id
                startDate
                name
              }
            }
          }
          productStatuses {
            vnm {
              status
            }
          }
        }
      }
    }
  }
`;

export const GET_FUNNEL_SOURCE = gql`
  query getFunnelSource($userId: Int!) {
    originationSource(userId: $userId) {
      funnel
      id
    }
  }
`;

export const GET_PROMO_CODE = gql`
  query getPromoCode($userId: Int!) {
    user(id: $userId) {
      signupPromoCode {
        code
      }
    }
  }
`;

export const getOriginationSourceMock = {
  originationSource: {
    funnel: 'waitlist',
    id: 5,
  },
};

export const getEnergyWellPromoCodeMock = {
  user: {
    signupPromoCode: {
      code: 'energywell',
    },
  },
};

export const getNoCommsEnergyWellPromoCodeMock = {
  user: {
    signupPromoCode: {
      code: 'no-comms-welcome-purchased-energywell-0323',
    },
  },
};

export const getAmbitPromoCodeMock = {
  user: {
    signupPromoCode: {
      code: 'ambit',
    },
  },
};

export const mockDashboardNotification = {
  __typename: 'Notification',
  actionType: 'go_to_vnm_terms',
  createdAt: '2021-01-10',
  displayValues: {
    buttonText: 'click me',
    message: 'A notification for ya',
    title: 'Look at this!',
  },
  id: 1,
  notificationKey: 'sign_terms',
  notifiableId: 1,
  notifiablePlanId: 20,
  priorityType: 'high_priority_task_error',
  read: false,
  targetId: 234,
};

const utility = {
  __typename: 'Utility',
  displayName: 'Test Utility',
  id: 1,
};

export const utilityAccounts = [
  {
    __typename: 'UtilityAccount',
    id: 5,
    accountNumber: '1111111',
    displayServiceAddress: '123 Test DR Fake, Fk 20000',
    highlights: ['Gas Account'],
    utility,
  },
];

const mostRecentStatementMock = {
  id: 1,
  firstStatementWithSolarSavings: false,
  lineItemSummary: {
    headlineSolarSavingsRate: 5,
  },
  status: 'not issued',
};

const vnmProductSubscriptionMock = {
  id: 1,
  vnmSubscription: {
    id: 2,
    status: 'pending',
    vnmSolarProject: {
      id: 100,
      name: 'Goat Island',
      startDate: '2022-01-01',
    },
  },
};

const vnmProductStatusMock = {
  vnm: {
    status: 'active',
  },
};

export const firstActivePlan = {
  __typename: 'Plan',
  displayName: 'My first active plan',
  createdAt: '2020-04-12T04:00:00.000Z',
  accountId: 5,
  mostRecentStatement: mostRecentStatementMock,
  id: 20,
  billable: true,
  utilityAccounts,
  status: 'active',
  productStatuses: vnmProductStatusMock,
  vnmProductSubscription: vnmProductSubscriptionMock,
  reactivationEligibility: {
    eligible: true,
    reasons: [],
  },
};

const secondActivePlan = {
  __typename: 'Plan',
  displayName: 'My second active plan',
  createdAt: '2020-04-12T04:00:00.000Z',
  accountId: 5,
  mostRecentStatement: mostRecentStatementMock,
  id: 21,
  billable: true,
  utilityAccounts,
  status: 'active',
  productStatuses: vnmProductStatusMock,
  vnmProductSubscription: vnmProductSubscriptionMock,
};

export const inactivePlan = {
  __typename: 'Plan',
  displayName: 'My inactive plan',
  createdAt: '2020-04-12T04:00:00.000Z',
  accountId: 2,
  mostRecentStatement: mostRecentStatementMock,
  id: 22,
  billable: true,
  utilityAccounts,
  status: 'inactive',
  productStatuses: vnmProductStatusMock,
  vnmProductSubscription: vnmProductSubscriptionMock,
};

export const searchPlansMock = {
  search: {
    plans: {
      plans: [firstActivePlan, secondActivePlan],
    },
  },
};

export const searchPlansInactiveMock = {
  search: {
    plans: {
      plans: [inactivePlan],
    },
  },
};

export const getDashboardNotificationsMock = {
  user: {
    __typename: 'User',
    firstName: 'Greta',
    dashboardNotifications: [mockDashboardNotification],
    id: 10,
  },
};

export const GET_ACCOUNT_IMPACT_STATS = gql`
  query getAccountImpactStats($id: Int!) {
    account(id: $id) {
      id
      accountImpactStats {
        vnmSavingsExpected
        vnmSavingsCumulativeCents
        vnmSavingsLastStatementCents
        vnmKwhExpected
        vnmKwhCumulative
        vnmKwhLastStatement
      }
    }
  }
`;

export const getAccountImpactStatsMock = {
  account: {
    __typename: 'Account',
    id: 2,
    accountImpactStats: {
      vnmKwhCumulative: 1000,
      vnmKwhExpected: true,
      vnmKwhLastStatement: 100,
      vnmSavingsCumulativeCents: 400,
      vnmSavingsExpected: true,
      vnmSavingsLastStatementCents: 100,
    },
  },
};

export const GET_ACCOUNT_SUMMARY = gql`
  query getAccountSummary($accountId: Int!) {
    account(id: $accountId) {
      id
      accountSummary {
        utilityAccountNumber
        serviceAddress
        associatedSolarFarm
        annualizedUsageEstimate
        vnmSubscriptionStatus
        commercialOperationDate
        accountBusinessName
        utilityCompany
        rateClass
        discountPercentage
        paymentSummary
        planName
      }
    }
  }
`;

export const getAccountSummaryMock = {
  account: {
    __typename: 'Account',
    id: 2,
    accountSummary: [
      {
        utilityAccountNumber: '12345',
        serviceAddress: '123 Test DR Fake, Fk 20000',
        associatedSolarFarm: 'Goat Island',
        annualizedUsageEstimate: 1000,
        vnmSubscriptionStatus: 'active',
        commercialOperationDate: '2022-01-01',
        accountBusinessName: 'Test Business',
        utilityCompany: 'Test Utility',
        rateClass: 'Test Rate',
        discountPercentage: 5,
        paymentSummary: 'Paid',
      },
    ],
  },
};
